"use client"
import Link from "next/link";
import { useMemo } from "react";
import { usePathname } from "next/navigation";
import { Star } from "lucide-react";
import useProduct from "@core/helpers/client/hooks/useProduct";
import PriceComponent from "@/components/ui/Price";
import MediaComponent from "@core/helpers/client/components/media-component";
import useCountDown from "@core/helpers/client/hooks/useCountDown";
import OfferLabels from "./OfferLabels";
import LIkeButtonProduct from "./LikeButtonProduct";
import { BrandLabel } from "./pages/SingleProduct/_brandLabel";
import useHydrated from "@core/helpers/client/hooks/useIsHydrated";
import { with_presale_stock } from "@/helpers/presale";

export function ProductCardSkeleton({ variant = "grid" }) {
    const pathname = usePathname();
    const isSearch = pathname?.startsWith("/search/");

    if (variant === "list") {
        return (
            <div className="w-full p-1 lg:p-2">
                <div className="h-6 mb-1" />
                <div className="flex justify-between gap-1">
                    <div className="shrink-0 w-[120px] xl:w-[136px]">
                        <div className="w-[120px] h-[120px] rounded-md shimmer-animation mx-auto" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0 gap-2">
                        <div className="h-4 lg:h-5 w-1/3 rounded-md shimmer-animation" />
                        <div className="h-4 lg:h-5 w-full rounded-md shimmer-animation" />
                        <div className="h-4 lg:h-5 w-2/3 rounded-md shimmer-animation" />
                        <div className="h-6 w-1/3 rounded-md shimmer-animation mt-auto ms-auto" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full bg-jmt-white p-1 lg:p-2 flex flex-col">

            {/* label & timer */}
            <div className="h-4.5 lg:h-5" />

            {/* image */}
            <div className={`w-full rounded-md shimmer-animation ${isSearch ? "h-[200px] lg:h-[216px]" : "h-[136px] lg:h-[190px]"}`} />

            {/* brand */}
            <div className="h-4 lg:h-5 w-1/2 mb-1 mt-1 rounded-md shimmer-animation" />

            {/* title */}
            <div className="min-h-8 lg:min-h-10 flex flex-col gap-1">
                <div className="h-3 lg:h-4 w-full rounded-md shimmer-animation" />
                <div className="h-3 lg:h-4 w-2/3 rounded-md shimmer-animation" />
            </div>

            {/* price */}
            <div className="w-full mt-auto pt-2">
                <div className="h-6 w-1/2 ms-auto rounded-md shimmer-animation" />
            </div>
        </div>
    );
}

export default function ProductCard({ product: raw_product, variant = "grid", favorite_page = false, }) {

    // واریانتِ پیش‌فروش موجودیِ صفر دارد ولی قیمت دارد و قابل سفارش است؛ مثل صفحه‌ی
    // محصول، `count` را با `presale.remaining` جایگزین می‌کنیم تا هسته آن را «ناموجود»
    // نبیند و برچسب پیش‌فروش هم بخورد. (کارت فقط وقتی `presale` را دارد که مدلش
    // `product_card` ژومنت باشد.)
    const product = useMemo(() => with_presale_stock(raw_product), [raw_product]);

    const {
        check_product_availability,
        check_product_offer,
        selected_label_keys,
        add_to_favorite_loading,
        add_product_to_favorites_handler,
        is_favorite
    } = useProduct({ ...product, id: product?.product_id || product?.id });

    const pathname = usePathname();
    const isSearch = pathname?.startsWith("/search/");
    const isList = variant === "list";

    const is_available = check_product_availability(product?.mix_variant?.[0]);
    const has_offer = check_product_offer(product?.mix_variant?.[0]);
    const discountPercent = product?.mix_variant?.[0]?.price?.discount_percent;
    // همان قاعده‌ی صفحه‌ی محصول (OfferBox و _addToCartDecktop): برچسب (پیش‌فروش،
    // شگفت‌انگیز، ...) فقط وقتی واریانت موجود یا قابل پیش‌فروش است. برچسبِ
    // پیش‌فروش از `presale.available` می‌آید (with_presale_stock)، پس با تمام شدن
    // پیش‌فروش خودش برداشته می‌شود؛ برچسبِ دستیِ پنل روی کالای ناموجود هم دیگر نمی‌ماند.
    const offerLabel = is_available
        ? product?.mix_variant?.[0]?.custom_fields?.custom_offer_label
        : undefined;

    // زمان فعلی فقط بعد از hydrate: Date.now() سرور و مرورگر فرق دارد و متن شمارنده hydration mismatch می‌ساخت
    const hydrated = useHydrated();
    const timer = useMemo(() => {
        if (!hydrated) return undefined;
        const expire_at = parseInt(product?.mix_variant?.[0]?.price?.offer_expireAt);
        const start_at = parseInt(product?.mix_variant?.[0]?.price?.offer_startAt);
        const now_time = new Date().getTime();

        if (
            product?.mix_variant?.[0]?.price?.show_discount_timer &&
            has_offer &&
            expire_at > 0 &&
            start_at > 0 &&
            is_available
        ) {
            return expire_at - now_time;
        }

        return undefined;
    }, [product, has_offer, is_available, hydrated]);

    const total_time = parseInt(product?.mix_variant?.[0]?.price?.offer_expireAt) -
        parseInt(product?.mix_variant?.[0]?.price?.offer_startAt);

    const remain_time = timer;

    const timer_percent = total_time > 0 && remain_time > 0
        ? (1 - remain_time / total_time) * 100
        : 0;

    const { counter_down, convert_countdown_time } = useCountDown({
        number: timer ? Math.floor(timer / 1000) : 0,
        countdown_className:
            "dir-ltr whitespace-nowrap h-[18px] lg:h-[20px] text-xs lg:text-sm font-bold text-jmt-red-700",
    });

    const colors = useMemo(() => {
        const arr = [];
        product?.variants?.forEach(variant => {
            if (variant?.variant?.key === "colors") {
                arr.push(...variant?.labels);
            }
        });
        return arr;
    }, [product?.variants]);

    const hrefPath = `${product?.seo?.full_url || ""}${selected_label_keys?.length > 0 ? `?selected-labels=${selected_label_keys}` : ""}`;

    // دکمه‌ی لایک در علاقه‌مندی‌های پروفایل، مثل کارت بلاگ روی گوشه‌ی عکس
    // می‌نشیند. داخل <Link> است، پس کلیک نباید به ناوبری برسد.
    const likeOverlay = favorite_page === true && (
        <div
            className="absolute top-2 left-2 z-20"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}>
            <LIkeButtonProduct
                data={product}
                is_favorite={is_favorite?.[product?.id]}
                add_to_favorite_loading={add_to_favorite_loading?.[product?.id]}
                add_product_to_favorites_handler={add_product_to_favorites_handler}
                className="flex items-center justify-center w-8 h-8 bg-jmt-white border border-jmt-neutral-200 hover:bg-jmt-neutral-100 rounded-full"
            />
        </div>
    );

    //  LIST VARIANT
    if (isList) {
        return (
            <div className="relative select-none p-1 lg:p-2 h-full">

                <Link href={hrefPath} title={product?.title} className="w-full h-full transition-shadow gap-1">

                    <div className={`flex items-center justify-between ${favorite_page === true ? "h-7" : ""}`}>

                        {/* amazing offer */}
                        {offerLabel && (
                            <div className="mb-1">
                                <OfferLabels offerLabel={offerLabel} size="lg" />
                            </div>
                        )}

                        {/* progressbar */}
                        <div className="flex items-center justify-between w-full gap-2 mb-1">

                            {timer > 0 ? (
                                <>
                                    <span className="text-[10px] lg:text-xs text-jmt-red-700 font-bold whitespace-nowrap">
                                        {timer_percent?.toFixed()}٪ فروش رفته
                                    </span>
                                    <div className="h-1 w-20 bg-neutral-200 rounded-lg overflow-hidden flex-1">
                                        <div
                                            className="h-1 bg-jmt-red-700 rounded-lg transition-all duration-300"
                                            style={{ width: `${timer_percent?.toFixed()}%` }}
                                        />
                                    </div>
                                    <div className="text-jmt-neutral-200">|</div>

                                    {/* timer */}
                                    <div className="flex items-center gap-1 font-bold text-xs lg:text-sm text-jmt-red-700 w-[45px]">
                                        {convert_countdown_time(counter_down, {
                                            formats: ["hours", "minutes", "seconds"],
                                            convertDaysToHours: true
                                        })}
                                    </div>
                                </>
                            ) : <div className="h-6"></div>}
                        </div>
                    </div>

                    <div className="flex justify-between gap-1">

                        {/* img & rate & colors */}
                        <div className="flex flex-col items-center shrink-0 w-[120px] xl:w-[136px] relative">

                            {/* img box */}
                            {/* عرض قاب باید صریح باشد. اگر فقط ارتفاع + aspect-square
                                بدهیم، سافاری (آیفون) عرض ستون را از عرض ذاتی خود فایل
                                تصویر (۳۵۰px) حساب می‌کند نه از نسبت ابعاد، ستون عکس
                                پهن می‌شود و ستون متن تا حد min-content له می‌شود. */}
                            <div className="relative flex items-center justify-center w-[120px] h-[120px] rounded-md overflow-hidden">
                                {likeOverlay}
                                <MediaComponent
                                    media={product?.media || product?.media_gallery?.[0]}
                                    width={100}
                                    height={100}
                                    // قاب ثابت ۱۲۰px؛ بدون sizes فایل ۷۵۰px دانلود می‌شد
                                    sizes="120px"
                                    style={{ width: "100%", height: "100%" }}
                                    className="object-cover"
                                />
                            </div>

                            {/* colors */}
                            {colors && colors.length > 0 && (
                                <div className="absolute right-0 top-3 flex flex-col gap-1 mt-2 z-10">
                                    {colors.slice(0, 3).map((color, index) => (
                                        <div
                                            key={index}
                                            className="w-2 h-2 rounded-full border border-jmt-neutral-200 overflow-hidden flex items-center justify-center shadow-sm"
                                            style={{
                                                backgroundColor: color?.value?.has_cover ? 'transparent' : (color?.value?.color || '#ccc')
                                            }}
                                            title={color?.title}
                                        >
                                            {color?.value?.has_cover && color?.value?.cover?.url ? (
                                                <img
                                                    src={color.value.cover.url}
                                                    alt={color.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : null}
                                        </div>
                                    ))}

                                    {colors.length > 3 && (
                                        <div className="w-2.5 h-2.5 flex items-center justify-center bg-jmt-white rounded-full border border-jmt-neutral-200 shadow-sm">
                                            <svg width="6" height="6" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1.66663 4H6.33329M3.99996 1.66667V6.33333" stroke="#171717" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* rate */}
                            {product?.average_rate > 0 && product?.has_rating ?
                                <div className="absolute left-0 bottom-0 flex items-center gap-1">
                                    <span className="text-xs text-gray-600">{product?.average_rate}</span>
                                    <Star size={12} fill="#FACC15" stroke="none" />
                                </div>
                                : ""}
                        </div>

                        {/* content */}
                        <div className="flex flex-col flex-1 min-w-0 justify-between">

                            {/* brand */}
                            {/* کل کارت داخل یک <Link> است، پس اینجا نباید لینک برند
                                بیاید: <a> تودرتو در html معتبر نیست و مرورگر موقع
                                هیدریت تگ بیرونی را می‌بندد و خطای hydration می‌دهد.
                                کارت گرید هم همین‌طور برند را متن ساده نشان می‌دهد. */}
                            <div className="flex items-center gap-1 mb-1">
                                {product?.brand?.title ? (
                                    <span className="font-bold text-xs lg:text-sm text-jmt-main">
                                        {product?.brand?.title}
                                    </span>
                                ) : ""}

                                <BrandLabel brand={product?.brand} />
                            </div>

                            {/* title */}
                            <div className="flex items-center">
                                <div className="text-jmt-neutral-800 text-xs lg:text-sm line-clamp-2">
                                    {product?.title}
                                </div>
                            </div>

                            {/* stock */}
                            {is_available &&
                                (product?.mix_variant?.[0]?.details?.count < 4 ||
                                    (
                                        product?.mix_variant?.[0]?.details?.limit_max &&
                                        product?.mix_variant?.[0]?.details?.limit_max < 4
                                    )
                                ) && (
                                    <div className="flex items-center gap-1 text-[10px] lg:text-xs text-jmt-red-700 mt-2">
                                        <p className="flex items-center gap-1">
                                            {
                                                product?.mix_variant?.[0]?.details?.count < 4
                                                    ? product?.mix_variant?.[0]?.details?.count
                                                    : product?.mix_variant?.[0]?.details?.limit_max
                                            }
                                            <span>  عدد باقیمانده </span>
                                        </p>
                                    </div>
                                )}

                            {/* price */}
                            <div className="mt-auto flex flex-col items-end gap-1">
                                {is_available ? (
                                    <>
                                        {has_offer ? (
                                            <>

                                                <div className="flex items-center gap-1.5">
                                                    <PriceComponent
                                                        price={product?.mix_variant?.[0]?.price?.price}
                                                        className="text-neutral-400 line-through text-xs lg:text-sm [&>.price-currency]:hidden"
                                                    />
                                                    {discountPercent && (
                                                        <div className="bg-jmt-red-700 text-white text-[11px] font-bold px-1.5 py-px rounded-md">
                                                            ٪{discountPercent}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="mt-0.5">
                                                    <PriceComponent
                                                        price={product?.mix_variant?.[0]?.price?.main_price}
                                                        className="font-extrabold text-sm lg:text-base text-neutral-900 [&>.price-currency]:mr-1"
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <div className="mt-1">
                                                <PriceComponent
                                                    price={product?.mix_variant?.[0]?.price?.main_price}
                                                    className="font-extrabold text-sm lg:text-base text-neutral-900 [&>.price-currency]:mr-1"
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-jmt-neutral-600 font-bold text-xs lg:text-sm">
                                        ناموجود
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>

                </Link>
            </div>

        );
    }

    // GRID VARIANT 
    return (
        <div className="relative w-full h-full bg-jmt-white p-1 lg:p-2">

            <Link
                href={hrefPath}
                title={product?.title}
                className="flex flex-col justify-start w-full h-full">

                {/* labal & timer */}
                {timer || offerLabel ?
                    <div className={`flex items-start pb-1 lg:pb-0 ${!timer ? "justify-start" : !offerLabel ? "justify-end" : "justify-between"}`}>
                        <OfferLabels offerLabel={offerLabel} size="sm" />
                        <div className="flex items-center">
                            {timer > 0 && (
                                <>
                                    {convert_countdown_time(counter_down, {
                                        formats: ["hours", "minutes", "seconds"],
                                        convertDaysToHours: true
                                    })}
                                </>
                            )}
                        </div>
                    </div>
                    : <div className="h-4.5 lg:h-5"></div>}

                {/* image & colors & rating */}
                <div className="relative flex justify-center">
                    {likeOverlay}
                    <div className={`w-full ${isSearch ? "h-[200px] lg:h-[216px]" : "h-[136px] lg:h-[190px]"}`}>
                        <MediaComponent
                            media={product?.media || product?.media_gallery?.[0]}
                            width={100}
                            height={100}
                            // عرض کارت (اندازه‌گیری‌شده در خانه و فهرست محصول): تا ~۴۰٪ عرض صفحه در موبایل، ~۲۸٪ تبلت،
                            // ~۱۸.۵٪ دسکتاپ (۳۴۹px در ۱۹۲۰). کمی بیشتر از بیشینه تا روی صفحه‌ی ۳x هم تیز بماند.
                            sizes="(max-width:767px) 45vw, (max-width:1023px) 30vw, 19vw"
                            style={{ width: "100%", height: "100%" }}
                            className="object-contain"
                        />
                    </div>

                    {/* color selector */}
                    {colors && colors.length > 0 && (
                        <div className="absolute right-0 top-2 flex flex-col gap-1">
                            {colors.slice(0, 4).map((color, index) => (
                                <div
                                    key={index}
                                    className="w-2 h-2 rounded-full border border-jmt-neutral-200 overflow-hidden flex items-center justify-center"
                                    style={{
                                        backgroundColor: color?.value?.has_cover ? 'transparent' : (color?.value?.color || '#ccc')
                                    }}
                                >
                                    {color?.value?.has_cover && color?.value?.cover?.url ? (
                                        <img
                                            src={color.value.cover.url}
                                            alt={color.title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : null}
                                </div>
                            ))}

                            {colors.length > 4 && (
                                <div className="w-2 h-2 flex items-center justify-center">
                                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1.66663 4H6.33329M3.99996 1.66667V6.33333" stroke="#171717" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    )}

                    {/* rating */}
                    {product?.average_rate > 0 && product?.has_rating ?
                        <div className="absolute left-0 bottom-0 flex items-center gap-1">
                            <span className="text-xs text-gray-600">{product?.average_rate}</span>
                            <Star size={12} fill="#FACC15" stroke="none" />
                        </div>
                        : ""}
                </div>

                {/* brand */}
                <div className="flex items-center gap-1 mb-1">
                    <span className="font-bold text-xs lg:text-sm"> {product?.brand?.title || 'برند نامشخص'} </span>
                    <BrandLabel brand={product?.brand} />
                </div>

                {/* title */}
                <div className="flex min-h-8 lg:min-h-10">
                    <div className="text-jmt-neutral-800 text-xs lg:text-sm leading-4 lg:leading-5 line-clamp-2">
                        {product?.title}
                    </div>
                </div>

                {/* stock */}
                {is_available &&
                    (product?.mix_variant?.[0]?.details?.count < 4 ||
                        (
                            product?.mix_variant?.[0]?.details?.limit_max &&
                            product?.mix_variant?.[0]?.details?.limit_max < 4
                        )
                    ) && (
                        <div className="flex items-center gap-1 text-[10px] lg:text-xs text-jmt-red-700 mt-2">
                            <p className="flex items-center gap-1">
                                {
                                    product?.mix_variant?.[0]?.details?.count < 4
                                        ? product?.mix_variant?.[0]?.details?.count
                                        : product?.mix_variant?.[0]?.details?.limit_max
                                }
                                <span>  عدد باقیمانده </span>
                            </p>
                        </div>
                    )}

                {/* old & final price & progressbar */}
                <div className="w-full mt-auto">
                    {is_available ?
                        <div className="mt-2 space-y-1 text-left">
                            {has_offer ? (
                                <>
                                    <div className="flex items-center justify-end gap-1">
                                        {/* old price */}
                                        <PriceComponent
                                            price={product?.mix_variant?.[0]?.price?.price}
                                            className="text-jmt-neutral-400 line-through text-sm [&>.price-currency]:hidden" />

                                        {/* offer badge */}
                                        {discountPercent && (
                                            <div className="flex items-center justify-center bg-jmt-red-700 rounded-md px-1 py-px">
                                                <span className="text-jmt-white text-xs font-bold">
                                                    {discountPercent}٪
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* final price */}
                                    <div className={`flex items-center gap-1 ${timer ? "justify-between" : "justify-end"}`}>
                                        {timer && (
                                            <span className="text-[10px] text-jmt-red-700">
                                                {timer_percent?.toFixed()}٪ فروش رفته
                                            </span>
                                        )}

                                        <PriceComponent
                                            price={product?.mix_variant?.[0]?.price?.main_price}
                                            className="font-bold text-base text-neutral-900"
                                        />
                                    </div>

                                    {timer && (
                                        <div className="h-1 bg-jmt-neutral-200 rounded-lg overflow-hidden">
                                            <div
                                                className="h-1 bg-jmt-red-700 rounded-lg transition-all duration-300"
                                                style={{ width: `${timer_percent?.toFixed()}%` }}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : <PriceComponent
                                price={product?.mix_variant?.[0]?.price?.main_price}
                                className="font-bold text-base text-neutral-900"
                            />}
                        </div>
                        : <div className="text-jmt-neutral-500 font-bold text-left text-sm">
                            <span> ناموجود </span>
                        </div>
                    }
                </div>
            </Link>
        </div>
    );
}
